import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Server-side pagination control.
 * Props:
 *   page          – current page (1-based)
 *   totalPages    – total number of pages
 *   total         – total items count
 *   pageSize      – items per page
 *   onPage(n)     – called when page changes
 *   onPageSize(n) – (optional) called when page-size changes
 *   testidPrefix  – prefix for data-testids (default "pagination")
 */
export default function Pagination({
  page = 1,
  totalPages = 1,
  total = 0,
  pageSize = 25,
  onPage,
  onPageSize,
  testidPrefix = "pagination",
}) {
  const safe = Math.max(1, totalPages || 1);
  const cur = Math.min(Math.max(1, page), safe);
  const start = total === 0 ? 0 : (cur - 1) * pageSize + 1;
  const end = Math.min(cur * pageSize, total);

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-ink-200 bg-stone-50/50"
      data-testid={`${testidPrefix}-bar`}
    >
      <div className="text-xs text-ink-500 font-mono">
        {total === 0
          ? "0 resultados"
          : <>Mostrando <span className="text-ink-900 font-bold">{start}–{end}</span> de <span className="text-ink-900 font-bold">{total}</span></>
        }
      </div>

      <div className="flex items-center gap-2">
        {onPageSize && (
          <select
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value))}
            className="text-xs bg-white border border-ink-200 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            data-testid={`${testidPrefix}-page-size`}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{`${n} / pág.`}</option>
            ))}
          </select>
        )}

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPage(cur - 1)}
            disabled={cur <= 1}
            className="p-1.5 rounded-md border border-ink-200 bg-white text-ink-700 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Página anterior"
            data-testid={`${testidPrefix}-prev`}
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs font-mono text-ink-700 px-2" data-testid={`${testidPrefix}-page-info`}>
            {cur} / {safe}
          </span>
          <button
            type="button"
            onClick={() => onPage(cur + 1)}
            disabled={cur >= safe}
            className="p-1.5 rounded-md border border-ink-200 bg-white text-ink-700 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Próxima página"
            data-testid={`${testidPrefix}-next`}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
